import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

function slugToName(slug: string): string {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function AreaPage() {
  const { city } = useParams<{ city: string }>();
  const cityName = city ? slugToName(city) : 'California';

  useSEO({
    title: `Real Estate in ${cityName}, CA | Homes For Sale | Jeffery Long eXp Realty`,
    description: `Explore homes for sale in ${cityName}, California with Jeffery Long at eXp Realty. Browse listings, get a free home valuation, and connect with a top-rated local real estate agent. CA DRE #02234432`,
    keywords: `${cityName} real estate, ${cityName} homes for sale, ${cityName} CA realtor, buy home ${cityName}, sell home ${cityName}, eXp Realty ${cityName}`,
    canonical: `/areas/${city}/ca`,
    ogTitle: `Real Estate in ${cityName}, CA | Jeffery Long eXp Realty`,
    ogDescription: `Find your dream home in ${cityName}, CA. Expert local real estate services from Jeffery Long with 40+ years of experience.`,
    ogImage: `https://public.readdy.ai/ai/img_res/e0cc930dfd02173649635b80096220e4.jpg$%7BcityName%7D%20California%20with%20tree%20lined%20streets%20charming%20homes%20and%20warm%20sunny%20skies%20clean%20suburban%20community&width=1200&height=600&seq=area-${city}&orientation=landscape`,
    schema: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      "name": "Jeffery Long - eXp Realty",
      "areaServed": {
        "@type": "City",
        "name": cityName,
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      },
      "telephone": "+1-916-257-8244",
      "email": "realestatedevelopmentauthority@gmail.com",
      "url": `https://jefferylong.exprealty.com`
    }
  });

  const services = [
    {
      icon: 'ri-home-search-line',
      title: 'Find Homes',
      desc: `Browse active listings in ${cityName} and surrounding neighborhoods with up-to-date MLS data.`,
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Free Home Valuation',
      desc: `Get an accurate, no-obligation estimate of your ${cityName} property's current market value.`,
    },
    {
      icon: 'ri-user-star-line',
      title: 'Local Expert',
      desc: `Work with a seasoned agent who knows the ${cityName} market inside and out — 40+ years of experience.`,
    },
    {
      icon: 'ri-bar-chart-2-line',
      title: 'Market Insights',
      desc: `Stay informed with the latest ${cityName} real estate trends, pricing data, and neighborhood reports.`,
    },
    {
      icon: 'ri-shake-hands-line',
      title: 'Negotiation Power',
      desc: `Our expert negotiators work tirelessly to get you the best deal whether buying or selling.`,
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Book a Consultation',
      desc: `Schedule a free, no-pressure consultation about buying or selling a home in ${cityName}.`,
    },
  ];

  const steps = [
    { num: '01', title: 'Contact Us', desc: `Reach out and tell us your goals — buying, selling, or both — in ${cityName}, CA.` },
    { num: '02', title: 'Property Search or Valuation', desc: 'We\'ll search listings that match your needs or provide a full market analysis of your current home.' },
    { num: '03', title: 'Tour & Evaluate', desc: `Schedule showings for your favorite ${cityName} properties and get expert guidance on each one.` },
    { num: '04', title: 'Make an Offer / List Your Home', desc: 'We handle the paperwork, negotiations, and strategy so you get the best outcome.' },
    { num: '05', title: 'Close with Confidence', desc: 'Our team guides you through every step to a smooth, successful closing.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative text-white py-24 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-top"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=beautiful%20residential%20neighborhood%20in%20$%7BcityName%7D%20California%20with%20tree%20lined%20streets%20charming%20homes%20and%20warm%20sunny%20skies%20clean%20suburban%20community&width=1200&height=600&seq=area-${city}&orientation=landscape')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60" />
          <div className="relative container mx-auto px-4 text-center w-full">
            <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-3">California Real Estate</p>
            <h1 className="text-5xl md:text-6xl font-bold mb-5 leading-tight">
              {cityName}, CA
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto mb-10">
              Your trusted real estate partner in {cityName}. Whether you&apos;re buying, selling, or investing — we&apos;re here to help every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://jefferylong.exprealty.com/index.php?showagent=1&rtype=map"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Search {cityName} Homes
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Talk to an Agent
              </Link>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="bg-gray-50 border-b border-gray-100 py-10">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center max-w-4xl mx-auto">
              <div>
                <p className="text-3xl font-bold text-emerald-600">40+</p>
                <p className="text-sm text-gray-500 mt-1">Years of Experience</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-600">500+</p>
                <p className="text-sm text-gray-500 mt-1">Homes Sold in CA</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-600">5★</p>
                <p className="text-sm text-gray-500 mt-1">Client Reviews</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-600">CA DRE</p>
                <p className="text-sm text-gray-500 mt-1">#02234432</p>
              </div>
            </div>
          </div>
        </section>

        {/* About This Area */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-emerald-600 text-sm font-semibold uppercase tracking-wider">Local Knowledge</span>
                <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-5">Real Estate in {cityName}, California</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {cityName} is one of the many vibrant communities we proudly serve throughout Northern and Central California. Whether you&apos;re looking to purchase your first home, upgrade to a larger property, or sell your current residence, the Jeffery Long team at eXp Realty is ready to deliver exceptional results.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  With decades of local expertise, we understand the unique characteristics of each California market — from pricing dynamics to neighborhood amenities. Our mission is to make your real estate journey in {cityName} as smooth and successful as possible.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/sell" className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer text-center">
                    Get a Free Home Value
                  </Link>
                  <Link to="/booking" className="border border-gray-300 hover:border-emerald-500 hover:text-emerald-600 text-gray-700 px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer text-center">
                    Book a Consultation
                  </Link>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden h-72">
                <img
                  src={`https://readdy.ai/api/search-image?query=aerial%20view%20of%20$%7BcityName%7D%20California%20city%20with%20beautiful%20homes%20neighborhoods%20parks%20and%20local%20landmarks%20on%20a%20clear%20day&width=700&height=500&seq=area-${city}-2&orientation=landscape`}
                  alt={`${cityName} California real estate neighborhood`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-emerald-600 text-sm font-semibold uppercase tracking-wider">What We Offer</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-2">Our Services in {cityName}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {services.map((s, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="w-11 h-11 flex items-center justify-center bg-emerald-50 rounded-lg mb-4">
                    <i className={`${s.icon} text-xl text-emerald-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-emerald-600 text-sm font-semibold uppercase tracking-wider">Simple Process</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-2">How It Works</h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-6">
              {steps.map((step, i) => (
                <div key={i} className="flex items-start gap-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-500 text-white font-bold text-sm shrink-0">
                    {step.num}
                  </div>
                  <div className="pt-1">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">{step.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Make Your Move in {cityName}?</h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Call or email Jeffery Long today for a free, no-obligation consultation about buying or selling real estate in {cityName}, CA.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:916-257-8244"
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>(916) 257-8244
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Send a Message
              </Link>
            </div>
            <p className="text-gray-500 text-xs mt-6">
              Jeffery Long · eXp Realty of Northern California, Inc. · CA DRE# 02234432
            </p>
          </div>
        </section>

        {/* Back to All Areas */}
        <section className="py-8 bg-gray-50 border-t border-gray-100">
          <div className="container mx-auto px-4 text-center">
            <Link to="/" className="text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors cursor-pointer">
              <i className="ri-arrow-left-line mr-1"></i>Back to All Areas We Cover
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
